import {
  Frame,
  FormLayout,
  TextField,
  Form,
  Button,
  Link,
  Text,
  Banner,
  Loading,
  InlineStack,
  Box,
  Popover,
  ActionList,
  Thumbnail,
} from '@shopify/polaris';
import 'media/css/login_register.scss';

import { useState, useCallback, useEffect } from 'react';
import LoginLogo from 'media/images/LoginLogo.svg';
import { lengthLessThan, lengthMoreThan, notEmptyString, useField, useForm } from '@shopify/react-form';

import helpers from '../helpers';

import __, { ___ } from 'languages/index';

import { ViewIcon, HideIcon, LanguageTranslateIcon, PersonLockFilledIcon } from '@shopify/polaris-icons';
import { Helmet } from 'react-helmet-async';
import { useUserLogin, useUserLogout } from 'queries/user.query';

export default function Login() {
  const { mutateAsync: login, isPending: loading } = useUserLogin();

  const __passport = (window as any).__passport || '';
  const [viewPasswordMode, setViewPasswordMode] = useState(false);

  const [internalErrorShow, setInternalErrorShow] = useState('');

  /** thông tin nội bộ */

  const clearInternalError = useCallback(() => {
    setInternalErrorShow('');
  }, []);

  /** Bắt đầu redirect ở đây... */
  const redirectTo = () => {
    const raw = process.env.REACT_APP_PUBLIC_URL || window.location.origin;
    let url = raw;
    try {
      url = decodeURIComponent(raw);
    } catch {
      // nếu raw đã là url bình thường hoặc có ký tự % lỗi thì bỏ decode
    }

    window.setTimeout(() => {
      window.location.replace(url); // replace để tránh back về trang login
    }, 1500); // muốn 3s thì đổi thành 3000
  };

  /**
   * Khai báo field cho form!
   */
  const useFields = {
    user_input: useField<string>({
      value: '',
      validates: [
        notEmptyString('Trường này không được để trống.'),
        lengthLessThan(50, 'Bạn điền quá dài!'),
        (inputValue) => {
          if (!helpers.isPhoneNumber(inputValue))
            if (!helpers.isEmail(inputValue)) {
              return 'Định dạng Email không hợp lệ! Vui lòng kiểm tra lại email của bạn!';
            }
          if (helpers.isUTF8(inputValue)) {
            return 'Trường này không nên có mã Unicode, bạn vui lòng kiểm tra lại!';
          }
        },
      ],
    }),
    password: useField<string>({
      value: '',
      validates: [
        notEmptyString('Trường này không được để trống.'),
        lengthMoreThan(6, 'Mật khẩu quá ngắn!'),
        (inputValue) => {
          if (helpers.isUTF8(inputValue)) {
            return 'Không nên dùng mã Unicode trong mật khẩu của bạn!';
          }
        },
      ],
    }),
  };

  const {
    fields,
    submit,
    reset: resetForm,
  } = useForm({
    fields: useFields,
    async onSubmit(form) {
      try {
        await login({
          user_input: form.user_input,
          password: form.password,
          device_type: 'website',
          device_signature: 'a_ignore' /** For notification, but website doesn't need it ... */,
          device_uuid: __passport,
        });
        redirectTo();
      } catch (e) {
        setInternalErrorShow(e?.message || 'Lỗi không xác định!');
      }
      return { status: 'success' };
    },
  });

  /** bắt buộc phải gọi logout để clear cookie */
  const { mutateAsync: logoutMeInLoginPage } = useUserLogout();
  useEffect(() => {
    resetForm();
    logoutMeInLoginPage();
    /** Migrate vào cái cũ */
    helpers.cookie_delete('AT', true);
    helpers.cookie_delete('AT', false);
    helpers.cookie_delete('RT', true);
    helpers.cookie_delete('RT', false);
    helpers.cookie_delete('EA', true);
    helpers.cookie_delete('EA', false);
  }, []);

  const [languagePopoverStatus, setLanguagePopoverStatus] = useState(false);
  const toggleLanguagePopoverActive = useCallback(() => setLanguagePopoverStatus((active) => !active), []);

  const setDefaultLanguage = useCallback((lang_code: string) => {
    helpers.cookie_set('language', lang_code);
    setLanguagePopoverStatus(false);
  }, []);

  return (
    <Frame>
      <Helmet>
        <title>{__('login_page_title')}</title>
      </Helmet>
      {loading ? <Loading /> : null}
      <div id="login_register_outer_wrap">
        <InlineStack blockAlign="center" align="center">
          <div id="login_page" style={{ maxWidth: '400px' }}>
            <InlineStack blockAlign="center" align="end" gap="200">
              <Popover
                preferredPosition="below"
                preventCloseOnChildOverlayClick
                active={languagePopoverStatus}
                activator={
                  <Button onClick={toggleLanguagePopoverActive} disclosure variant="plain" icon={LanguageTranslateIcon}>
                    {__('switch_language_button')}
                  </Button>
                }
                autofocusTarget="first-node"
                onClose={toggleLanguagePopoverActive}
              >
                <ActionList
                  actionRole="menuitem"
                  items={[
                    {
                      content: 'Vietnamese',
                      onAction: () => setDefaultLanguage('vi'),
                      icon: LanguageTranslateIcon,
                    },
                    {
                      content: 'English',
                      onAction: () => setDefaultLanguage('en'),
                      icon: LanguageTranslateIcon,
                    },
                  ]}
                />
              </Popover>
            </InlineStack>

            <br />
            {internalErrorShow && (
              <>
                <Banner tone="critical" onDismiss={clearInternalError}>
                  {internalErrorShow}
                </Banner>
                <br />
              </>
            )}

            <Box background="bg-fill" padding={'400'} borderRadius="400">
              <Form onSubmit={submit}>
                <InlineStack wrap={false} gap="200" blockAlign="center" align="start">
                  <div>
                    <Thumbnail size="medium" source={LoginLogo} alt={'Login Logo'} />
                  </div>
                  <div>
                    <Text as="h3" variant="headingLg" fontWeight="regular">
                      {__('welcome')}
                    </Text>
                    <Text as="h6" variant="bodySm" fontWeight="regular">
                      {__('login')}
                    </Text>
                  </div>
                </InlineStack>

                <br />

                <FormLayout>
                  <TextField
                    type="text"
                    disabled={loading}
                    placeholder=""
                    label={__('login_by_your_account')}
                    {...fields.user_input}
                    requiredIndicator
                    autoComplete="off"
                  />
                  <TextField
                    disabled={loading}
                    label={__('login_by_your_password')}
                    placeholder="******"
                    type={viewPasswordMode ? 'text' : 'password'}
                    requiredIndicator
                    autoComplete="off"
                    suffix={
                      <InlineStack blockAlign="center">
                        <Button
                          variant="plain"
                          onClick={() => setViewPasswordMode(!viewPasswordMode)}
                          icon={viewPasswordMode ? ViewIcon : HideIcon}
                        ></Button>
                      </InlineStack>
                    }
                    {...fields.password}
                  />

                  <Button
                    icon={PersonLockFilledIcon}
                    submit
                    variant="primary"
                    fullWidth
                    disabled={loading}
                    onClick={submit}
                    key={'button_login_normal'}
                    size="large"
                  >
                    {__('login_button_context')}
                  </Button>

                  <Text as="p" key="C">
                    {___('Forgot your password? {recover_link} to recover!', {
                      recover_link: <Link url="/recover_password">{__('click_here')}</Link>,
                    })}
                  </Text>
                </FormLayout>
              </Form>
            </Box>
          </div>
        </InlineStack>
      </div>
    </Frame>
  );
}
